import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/lib/LanguageContext";
import { formatDate } from "../../lib/dateTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { TRIAL_PATHS, TRIAL_EXTEND_MIN_DAYS, TRIAL_EXTEND_MAX_DAYS } from "../../saas/trial/contract";
import { trialErrorText } from "../../saas/trial/errorText";

/**
 * TrialTile — Tenant360's own read of `/360.trial`, plus the two super-admin actions the
 * wire contract exposes (§4): extend the trial window, or end it now (which lets Stripe
 * invoice immediately and the practice convert on `invoice.paid`). Both are POSTs, both
 * are wrapped in try/catch (never a silent failure — `silentWriteHandlers.static.test.js`
 * holds this file to the same zero-offender bar as every other write in the console), and
 * both refusals are read through `trialErrorText` so a 409/503 renders a sentence, never
 * `detail.message` (which is English and a developer diagnostic).
 *
 * Extend/End are shown only while `trialing`: a converted or ended trial has nothing left
 * for either action to do, and `TRIAL_NOT_ACTIVE` is exactly the 409 the backend would
 * otherwise answer. They are also shown only when `canAct`: both routes are tenants.write,
 * and the page passes whether the operator holds it (screenWrites.js, `tenant360.trial`).
 */
export default function TrialTile({ trial, tenantId, tenantName, t, canAct = false, onChanged }) {
  const { lang } = useLanguage();
  const [days, setDays] = useState(String(TRIAL_EXTEND_MIN_DAYS + 6));
  const [busy, setBusy] = useState(false);

  const state = trial?.state || "none";
  const fmtDate = (iso) => formatDate(iso, lang);

  const daysNumber = Number(days);
  const daysValid =
    Number.isInteger(daysNumber) && daysNumber >= TRIAL_EXTEND_MIN_DAYS && daysNumber <= TRIAL_EXTEND_MAX_DAYS;

  const extend = async () => {
    if (!daysValid) return;
    setBusy(true);
    try {
      await api.post(TRIAL_PATHS.superAdminExtend(tenantId), { days: daysNumber });
      toast.success(t("t360.trial.extended", { days: daysNumber }));
      onChanged?.();
    } catch (e) {
      toast.error(trialErrorText(e, { t, prefix: "t360.trial", fallback: t("t360.error") }));
    } finally {
      setBusy(false);
    }
  };

  const endNow = async () => {
    if (!window.confirm(t("t360.trial.endConfirm", { name: tenantName }))) return;
    setBusy(true);
    try {
      await api.post(TRIAL_PATHS.superAdminEnd(tenantId));
      toast.success(t("t360.trial.ended"));
      onChanged?.();
    } catch (e) {
      toast.error(trialErrorText(e, { t, prefix: "t360.trial", fallback: t("t360.error") }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="t360-trial-tile">
      <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t("t360.trial.title")}</div>
      <div className="font-sol font-semibold text-xl text-slate-800" data-testid="t360-trial-state">
        {t(`t360.trial.state.${state}`)}
      </div>
      {state === "trialing" && Number.isInteger(trial.days_remaining) && (
        <div className="text-xs text-slate-500 mt-1" data-testid="t360-trial-days">
          {t("t360.trial.daysLeft", { days: trial.days_remaining })}
        </div>
      )}
      {/* An early end keeps the recorded, still-future `ends_at`: "Ends on {date}" under
          "Trial ended" would contradict the state, so the server's `ended_early` picks the
          sentence that names the date as the scheduled one. */}
      {trial?.ends_at && (
        <div className="text-xs text-slate-400 mt-1" data-testid="t360-trial-ends">
          {state === "ended" && trial.ended_early
            ? t("t360.trial.endedEarly", { date: fmtDate(trial.ends_at) })
            : t("t360.trial.endsOn", { date: fmtDate(trial.ends_at) })}
        </div>
      )}
      {trial?.converted_at && (
        <div className="text-xs text-slate-400 mt-1">
          {t("t360.trial.convertedOn", { date: fmtDate(trial.converted_at) })}
        </div>
      )}
      {state === "trialing" && canAct && (
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <Input
            type="number"
            min={TRIAL_EXTEND_MIN_DAYS}
            max={TRIAL_EXTEND_MAX_DAYS}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            data-testid="t360-trial-extend-days"
            className="w-20 bg-white border-slate-200"
          />
          <Button size="sm" variant="outline" onClick={extend} disabled={busy || !daysValid} data-testid="t360-trial-extend">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("t360.trial.extend")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={endNow}
            disabled={busy}
            className="border-rose-200 text-rose-600 hover:bg-rose-50"
            data-testid="t360-trial-end"
          >
            {t("t360.trial.end")}
          </Button>
        </div>
      )}
    </div>
  );
}
